import {ExternalLinkHandlerBase} from "./external-link-handler-base";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IExternalLinkRenderer} from "../renderers/external-link-renderer.interface";
import {
    ExternalLinkIframeRenderer,
    ExternalLinkIframeRendererOptions
} from "../renderers/external-link-iframe.renderer";
import {GNWP_SOURCES} from "../../../../sdk/src/components/external_links/external_links_consts";

export type ExternalLinkInIframeRendererParams = Omit<ExternalLinkIframeRendererOptions, 'onClosed'>;

export abstract class ExternalLinkInIframeHandlerBase extends ExternalLinkHandlerBase {
    constructor(services: IServiceFactoryExtended) {
        super(services);
        this.addSdkEventSubscription(this.services.sdk.events.onPostMessage(this._handlePostMessage));
    }

    protected abstract _onPostMessageReceived(message: MessageEvent): Promise<void>;
    protected abstract _getGnwpSource(): GNWP_SOURCES;

    private _handlePostMessage = async (message: MessageEvent) => {
        if(message.source !== this._renderer?.iframeElementRef?.contentWindow) {
            return;
        }

        this._sendAnalytics(message);
        await this._onPostMessageReceived(message);
    }

    protected _sendAnalytics(message: MessageEvent): void {
        const action = `ExternalLink ${this._getGnwpSource()}`;
        if(message.data) {
            this.services.analytics.sendTechnicalEvent(action, `PostMessage: ${JSON.stringify(message.data)}`);
        } else {
            this.services.analytics.sendTechnicalEvent(action);
        }
    }

    protected _renderer: ExternalLinkIframeRenderer | null = null;
    protected _getRenderer(paramsOverride?: Partial<ExternalLinkInIframeRendererParams>): IExternalLinkRenderer {
        if(!this._renderer) {
            this._renderer = new ExternalLinkIframeRenderer(this.services, {
                ...this._getIframeRendererParams(),
                ...paramsOverride,
                onClosed: this._onExternalLinkClosed
            });
        }

        return this._renderer;
    }

    protected abstract _getIframeRendererParams(): ExternalLinkInIframeRendererParams;

    protected _onExternalLinkClosed = async () => {
        this.services.sdk.eventsTrigger.externalLinkClosed({
            gnwpSource: this._getGnwpSource()
        });
        this.dispose();
    }

    protected _closeExternalLinkDialog = () => {
        this._renderer?.dispose();
        this._renderer = null;
    }

    protected async _showExternalLinkDialog(url: string, paramsOverride?: Partial<ExternalLinkInIframeRendererParams>): Promise<void>  {
        await this._getRenderer(paramsOverride).render(url)
    }
}